// 路由 ↔ 權限代碼；存取判斷見 @/constants/permissionRules
import {
    ICE_FIRE_SUPER,
    canAccessPermissionCode,
    isIceFireGuardedRoute,
    getPermissionCodeFromRecord,
    buildOwnedCodeSet,
    MEMBER_DETAIL_ACCESS_CODES,
    MEMBER_LIST_ACCESS_CODES
} from '@/constants/permissionRules';
import { WORKSPACE, WORKSPACE_ENTRY_CODES } from '@/constants/roleWorkspaceMatrix';

/** admin 角色：後端 permission:sync 會掛全量權限，前端側欄／路由一併全開 */
export const isFullAccessAdminRole = (roles = []) =>
    (roles || []).some((r) => {
        const name = typeof r === 'string' ? r : r?.name;
        return String(name || '').toLowerCase() === 'admin';
    });

const isFullAccess = (options = {}) =>
    options.fullAccess === true || isFullAccessAdminRole(options.roles);
// 權限對應表
export const routePermissions = {
    Members: ['100000', ...MEMBER_LIST_ACCESS_CODES, ...MEMBER_DETAIL_ACCESS_CODES],
    // 系統營運：含原平台管理葉碼（見 WORKSPACE_ENTRY_CODES.SYSTEM_OPS）；稽核獨立
    MemberMgmt: WORKSPACE_ENTRY_CODES[WORKSPACE.SYSTEM_OPS],
    // 夥伴營運：不含 100101／100102／100110 等共用葉碼（避免系統營運角色誤開）
    MembersBusiness: ['100118'],
    MembersGeneralAdmin: WORKSPACE_ENTRY_CODES[WORKSPACE.USER_ADMIN],
    MembersList: ['100000'],
    CreateMember: '100101',
    MemberDetail: MEMBER_DETAIL_ACCESS_CODES,

    PartnerOps: WORKSPACE_ENTRY_CODES[WORKSPACE.PARTNER_OPS],
    PartnerOpsAttendance: [
        '210000',
        '211000',
        '211001',
        '211002',
        '211003',
        '211004',
        '211005',
        '211006',
        '211011',
        '211012',
        '211013'
    ],
    PartnerOpsLegacyStudents: ['210000', '212000', '212001', '212002'],
    PartnerOpsAccessBlocklist: ['210000', '213000', '213001', '213002'],
    PartnerOpsCourseIntake: ['210000', '214000', '214001'],

    Permission: '990000',
    // 平台管理 folder 已廢除；別名對齊系統營運入口碼
    Platform: WORKSPACE_ENTRY_CODES[WORKSPACE.SYSTEM_OPS],
    AdminList: ['990201', '990202', '990203', '990204', '990205'],
    RoleList: ['990101', '990102', '990103', '990104'],

    // 操作紀錄：單層選單，不依賴系統營運權限
    Audit: ['200000', '200101'],
    AuditLog: ['200000', '200101'],
    AuditLogExport: '200102',

    RagicIntegration: ['900400', '900401', '900402'],
    ExternalIntegrations: ['900400', '900401', '900402', '900800', '900801'],
    /** 修業進路：獨立權限（側欄系統營運） */
    CoursePaths: [
        '900500',
        '900501',
        '900502',
        '900503',
        '900504',
        '900505'
    ],
    /** 會員入口授權（中樞廳） */
    MemberEntryAccess: ['100121', '100117'],
    /** 完課補發與商品限購占用（系統營運） */
    OpsCompletionPurchaseGrant: ['100122', '100123'],
    /** 產業鏈主檔（系統營運） */
    IndustryChains: ['100130', '100131', '100132', '100133'],
    /** 背包物品類型／服務白名單（系統營運） */
    InventoryItemTypes: [
        '190700',
        '190701',
        '190702',
        '190703',
        '190704'
    ],
    'SystemOps.Group.MemberEntry': [
        '100000',
        '100121',
        '100117',
        '100122',
        '100123',
        ...MEMBER_LIST_ACCESS_CODES
    ],
    'SystemOps.Group.Industry': ['100130', '100131', '100132', '100133'],
    'SystemOps.Group.Course': [
        '900500',
        '900501',
        '900502',
        '900503',
        '900504',
        '900505'
    ],
    'SystemOps.Group.Accounts': [
        '990201',
        '990202',
        '990203',
        '990204',
        '990205',
        '990101',
        '990102',
        '990103',
        '990104'
    ],
    'SystemOps.Group.Integrations': [
        '900300',
        '900301',
        '900302',
        '900303',
        '900304',
        '900400',
        '900401',
        '900402',
        '900800',
        '900801',
        '190700',
        '190701',
        '190702',
        '190703',
        '190704'
    ],

    Points: WORKSPACE_ENTRY_CODES[WORKSPACE.POINTS],
    PointsList: ['110102', '110103'],
    PointHistory: '110200',

    Levels: WORKSPACE_ENTRY_CODES[WORKSPACE.LEVELS],
    LevelSetting: ['120101', '120102', '120103', '120104'],
    LevelLog: '120200',

    Missions: '130000',
    MissionsList: ['130101', '130102', '130103', '130104'],
    MissionRecords: ['130201', '130202'],
    MissionReviews: ['130301', '130302', '130303', '130304'],
    MissionCreate: '130101',
    MissionEdit: '130102',

    // 商城目錄 vs 訂單工作面（父層閘門分離）
    Products: WORKSPACE_ENTRY_CODES[WORKSPACE.SHOP_CATALOG],
    OrderDesk: WORKSPACE_ENTRY_CODES[WORKSPACE.ORDER_DESK],
    ProductsList: ['150201', '150202', '150203', '150204', '150205'],
    ShopVendors: ['150201', '150202', '150203', '150204', '150205'],
    // 背包管理 (190000)：父選單任一子權限即可顯示
    Inventory: [
        '190000',
        '190600',
        '190601',
        '190602',
        '190603',
        '190604',
        '190605',
        '190700',
        '190701',
        '190702',
        '190703',
        '190704',
        '190800',
        '190801',
        '190802',
        '190803',
        '190804',
        '190900',
        '190901'
    ],
    InventoryRedeemScan: ['190900', '190901'],
    InventoryItemTypeView: '190701',
    InventoryItemTypeCreate: '190702',
    InventoryItemTypeUpdate: '190703',
    InventoryItemTypeDelete: '190704',
    InventoryItems: [
        '190000',
        '190800',
        '190801',
        '190802',
        '190803',
        '190804',
        // 物品規格下拉需讀類型清單（唯讀）；開關仍僅系統營運頁
        '190701'
    ],
    InventoryItemView: '190801',
    InventoryItemCreate: '190802',
    InventoryItemUpdate: '190803',
    InventoryItemDelete: '190804',
    UserInventoryList: [
        '190000',
        '190600',
        '190601',
        '190602',
        '190603',
        '190604'
    ],
    UserInventory: '190601',
    UserInventoryGrant: '190602',
    UserInventoryUpdate: '190603',
    UserInventoryDelete: '190604',
    ProductCreate: '150201',
    ProductEdit: '150202',
    ProductCategories: ['150101', '150102', '150103', '150104'],
    ProductsCategories: ['150101', '150102', '150103', '150104'],
    /** 購買前同意／契約模板：對齊商品 CRUD API（150201–150204） */
    PurchaseRequirementSettings: [
        '150201',
        '150202',
        '150203',
        '150204',
        '150205'
    ],
    /** 契約簽署紀錄：對齊 EnrollmentFlowController（150204 檢視） */
    ContractSignatures: ['150204', '150205'],
    OrdersList: ['150301', '150302', '150303', '150304', '150305', '150306'],
    OrdersListReadonly: ['150301'],
    OrderCancellations: ['150501', '150502', '150503', '150504'],
    PaymentSettings: ['150401', '150402', '150403'],
    ShippingSettings: WORKSPACE_ENTRY_CODES[WORKSPACE.SHOP_CATALOG],
    VirtualReceipts: WORKSPACE_ENTRY_CODES[WORKSPACE.SHOP_CATALOG],
    FinanceOrders: ['150301', '150302', '150304', '150305'],

    Settings: '900000',
    PaymentApi: ['900301', '900302', '900303', '900304'],
    Ranks: ['900201', '900202', '900203', '900204'],
    Departments: ['900101', '900102', '900103', '900104'],

    Quizzes: '140000',
    QuizzesManagement: ['140101', '140102', '140103', '140104'],
    QuizzesReviews: ['140202', '140204'],
    QuizzesAttempts: ['140302', '140304'],
    QuizzesCreate: '140101',
    QuizzesEdit: '140102',
    QuizzesView: '140104',
    QuizzesSetAnswers: '140102',
    QuizzesAttemptView: '140304',

    // 表單管理 (180000+)：父選單任一子權限即可顯示
    Forms: [
        '180000',
        '180101',
        '180102',
        '180103',
        '180104',
        '180201',
        '180202',
        '180203',
        '180204',
        '180301',
        '180302',
        '180303',
        '180304',
        '180305',
        '180401',
        '180402',
        '180403',
        '180404',
        '180405',
        '180501',
        '180502',
        '180503',
        '180504',
        '180601',
        '180602',
        '150201',
        '150202',
        '150203',
        '150204',
        '150205'
    ],
    FormsManagement: [
        '180000',
        '180100',
        '180101',
        '180102',
        '180103',
        '180104'
    ],
    FormsCreate: '180101',
    FormsEdit: '180102',
    FormSubmissions: ['180000', '180200', '180201', '180202', '180204'],
    AttendeeManagement: ['180000', '180200', '180201', '180203'],
    /** 表單詳情：具備檢視或任一營運權限即可進入 */
    FormsView: [
        '180000',
        '180100',
        '180104',
        '180200',
        '180201',
        '180202',
        '180203',
        '180204'
    ],
    FormTopics: [
        '180000',
        '180500',
        '180501',
        '180502',
        '180503',
        '180504'
    ],
    FormTopicsCreate: '180501',
    FormTopicsEdit: '180502',
    FormTopicsDelete: '180503',
    FormActivities: [
        '180000',
        '180300',
        '180301',
        '180302',
        '180303',
        '180304',
        '180305',
        '180400',
        '180401',
        '180402',
        '180403',
        '180404',
        '180405'
    ],
    FormActivitiesCreate: '180301',
    FormActivitiesEdit: '180302',
    FormActivitiesDelete: '180303',
    FormCancellationRequests: [
        '180000',
        '180600',
        '180601',
        '180602'
    ],

    // 冰火：170000 super；群組碼與子頁碼皆可單獨開啟側欄
    IceFire: [
        '170000',
        '171000',
        '171100',
        '171200',
        '171300',
        '171400',
        '171411',
        '171412',
        '171413',
        '171500',
        '171501',
        '171502',
        '171503',
        '171600',
        '171601',
        '171602',
        '171700'
    ],
    'IceFire.Dashboard': '171000',
    'IceFire.ActivitySettings': '171100',
    'IceFire.Teams.List': '171200',
    'IceFire.Participants.List': '171200',
    'IceFire.DepartmentTeamMap.List': '171200',
    'IceFire.Rounds.List': '171100',
    'IceFire.Rounds.Settle': '171100',
    'IceFire.Courses.List': '171300',
    'IceFire.ProductCourseMap.List': '171300',
    'IceFire.ItemPointsConfig.List': '171300',
    'IceFire.TriggerRules.List': '171411',
    'IceFire.TriggerRules.Create': '171411',
    'IceFire.TriggerRules.Edit': '171411',
    'IceFire.TriggerRuleEvents.List': '171412',
    'IceFire.ScheduledEvents.List': '171413',
    'IceFire.Recruitments.List': '171501',
    'IceFire.Consultations.List': '171502',
    'IceFire.CourseEnrollments.List': '171503',
    'IceFire.ManualTasks.List': '171601',
    'IceFire.BatchImports.List': '171602',
    'IceFire.BatchImports.Detail': '171602',
    'IceFire.ScoreLedger': '171700',
    'IceFire.Group.Records': ['171500', '171501', '171502', '171503'],
    'IceFire.Group.Scoring': ['171600', '171601', '171602'],
    'IceFire.Group.Rules': ['171400', '171411', '171412', '171413'],
    'IceFire.Group.System': ['171100', '171200', '171300'],
    'Policy.List': '171800',

    Dashboard: [
        '160001',
        '160002',
        '160004',
        '160005',
        '160006',
        '160007',
        '160008'
    ]
};

const hasPermissionCode = (permissions, targetCode, options) => {
    const owned = buildOwnedCodeSet(permissions);
    return canAccessPermissionCode(targetCode, owned, options);
};

export const checkRoutePermission = (routeName, permissions, options = {}) => {
    if (isFullAccess(options)) {
        return true;
    }

    const forMenu = options.forMenu === true;

    if (!routePermissions[routeName]) {
        return isIceFireGuardedRoute(routeName) ? false : true;
    }

    const required = routePermissions[routeName];

    if (Array.isArray(required)) {
        return required.some((code) => {
            if (forMenu && String(code) === ICE_FIRE_SUPER) {
                return false;
            }
            return hasPermissionCode(permissions, code, options);
        });
    }

    return hasPermissionCode(permissions, required, options);
};

export const filterRoutesByPermissions = (
    routes,
    permissions,
    options = {}
) => {
    const fullAccess = isFullAccess(options);

    return routes
        .map((route) => {
            if (route.meta && route.meta.hidden) return null;

            // 先濾子路由，避免「父模組碼缺失」整包選單消失（如背包 190000）
            const children =
                Array.isArray(route.children) && route.children.length > 0
                    ? filterRoutesByPermissions(route.children, permissions, options)
                    : [];

            if (children.length > 0) {
                return { ...route, children };
            }

            if (fullAccess) {
                // 子路由皆 hidden（會員管理）：仍顯示父層
                const allChildrenHidden =
                    Array.isArray(route.children) &&
                    route.children.length > 0 &&
                    route.children.every((child) => child.meta?.hidden);

                if (allChildrenHidden || !route.children || route.children.length === 0) {
                    return { ...route, children: [] };
                }
                // 可見子項皆已被過濾的情況不應在 fullAccess 發生
                return null;
            }

            const hasParentPermission = checkRoutePermission(
                route.name,
                permissions,
                options
            );

            if (!hasParentPermission) {
                return null;
            }

            const hasAnyChildPermission =
                Array.isArray(route.children) && route.children.length > 0
                    ? route.children.some((child) =>
                          checkRoutePermission(child.name, permissions, options)
                      )
                    : false;

            if (hasAnyChildPermission) {
                return { ...route, children: [] };
            }

            // 子路由皆 hidden（如會員管理）：父層有權限時仍顯示為單層選單
            const allChildrenHidden =
                Array.isArray(route.children) &&
                route.children.length > 0 &&
                route.children.every((child) => child.meta?.hidden);

            if (allChildrenHidden) {
                return { ...route, children: [] };
            }

            if (!route.children || route.children.length === 0) {
                return { ...route, children: [] };
            }

            return null;
        })
        .filter((r) => r);
};

// 相容舊呼叫
export { getPermissionCodeFromRecord };

/** 全站無權限頁（路由守衛導向） */
export const ADMIN_NO_PERMISSION_ROUTE = 'Admin.NoPermission';

const PERMISSION_EXEMPT_ROUTES = new Set([
    ADMIN_NO_PERMISSION_ROUTE,
    'IceFire.NoPermission'
]);

/** 是否略過路由權限檢查（無權限頁、純 redirect 等） */
export const isPermissionExemptRoute = (routeName) =>
    routeName == null || PERMISSION_EXEMPT_ROUTES.has(routeName);

/**
 * 是否需檢查 routePermissions（含冰火／保單未定義路由一律拒絕）
 */
export const requiresRoutePermissionCheck = (routeName) => {
    if (isPermissionExemptRoute(routeName)) return false;
    if (typeof routeName !== 'string') return false;
    if (routePermissions[routeName] !== undefined) return true;
    return isIceFireGuardedRoute(routeName);
};
